import Cached from "@material-ui/icons/Cached";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import Repeat from "@material-ui/icons/Repeat";
import NumericTextbox from "Components/Common/Input/TextBoxes/NumericTextbox";
import ContentView from "Components/ContentView/ContentView";
import Datepicker from "Components/Datepicker/Datepicker";
import Dropdown from "Components/Dropdown/Dropdown";
import DropdownOption from "Components/Dropdown/DropdownOption/DropdownOption";
import ShiftCalendar from "Components/ShiftCalendar/ShiftCalendar";
import { ShiftCalendarMode } from "Components/ShiftCalendar/ShiftCalendarMode";
import TabPane from "Components/Tabview/Tabpane/TabPane";
import Tabview from "Components/Tabview/Tabview";
import { TabviewMode } from "Components/Tabview/TabviewMode";
import ToggleBar from "Components/ToggleBar/ToggleBar";
import ToggleOption from "Components/ToggleBar/ToggleOption/ToggleOption";
import { Area } from "Models/Areas/Area";
import { Shift } from "Models/Shifts/Shift";
import { TaskMonthRecurrency } from "Models/Tasks/TaskRecurrencies/TaskMonthRecurrency";
import { TaskRecurrency } from "Models/Tasks/TaskRecurrencies/TaskRecurrency";
import { TaskRecurrencyPeriodToggle } from "Models/Tasks/TaskRecurrencies/TaskRecurrencyPeriodToggle";
import { TaskRecurrencyType } from "Models/Tasks/TaskRecurrencies/TaskRecurrencyType";
import moment from "moment";
import * as React from "react";
import "react-datepicker/dist/react-datepicker.css";
import { connect } from "react-redux";
import { Action, Dispatch } from "redux";
import ServiceProvider from "ServiceProvider/ServiceProvider";
import Types from "ServiceProvider/Types";
import { IShiftAction } from "Store/Shifts/ShiftAction";
import { IShiftState } from "Store/Shifts/ShiftState";
import { IApplicationState } from "Store/Store";
import i18n from "Translations/i18n";
import styles from "./TaskTemplateRecurrencyView.scss";

const shiftAction = ServiceProvider.get<IShiftAction>(Types.IShiftAction);

interface IProps {
    area: Area | undefined;
    taskRecurrency: TaskRecurrency;
    shiftState: IShiftState;
    onRecurrencyChanged: (taskRecurrency: TaskRecurrency) => Action;
    hasAreaShiftsForCompanyAsync: () => Action;
    getCompanyShiftsAsync: () => Action;
    getAreaShiftsAsync: (areaId: number) => Action;
}

interface IState {
    taskRecurrency: TaskRecurrency;
    area: Area | undefined;
    hasAreaShifts: boolean | undefined;
    shifts: Shift[];
    companyShifts: Shift[];
    areaShifts: Shift[];
    selectedPeriodToggleKey: string;
    selectedMonthlyTabKey: string;
    cachedFromDate?: Date;
    cachedUntilDate?: Date;
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
    hasAreaShiftsForCompanyAsync: () =>
        dispatch(shiftAction.hasAreaShiftsForCompanyAsync()),
    getCompanyShiftsAsync: () =>
        dispatch(shiftAction.getCompanyShiftsAsync()),
    getAreaShiftsAsync: (areaId: number, ) =>
        dispatch(shiftAction.getAreaShiftsAsync(areaId))
});

const mapStateToProps = (state: IApplicationState) => ({
    shiftState: state.shiftState
});

export class TaskTemplateRecurrencyView extends React.Component<IProps, IState> {
    private dropdownOptionsDaysOfMonth: React.ReactNode[] = [];
    private dropdownOptionsMonthsOfYear: React.ReactNode[] = [];
    private dropdownOptionsWeedayOfMonth: React.ReactNode[] = [];
    private dropdownOptionsWeekdayString: React.ReactNode[] = [];

    constructor(props: IProps) {
        super(props);

        this.state = {
            shifts: [],
            companyShifts: [],
            areaShifts: [],
            taskRecurrency: props.taskRecurrency,
            area: props.area,
            hasAreaShifts: undefined,
            selectedPeriodToggleKey: this.determineInitialPeriodToggle(props.taskRecurrency),
            selectedMonthlyTabKey: props.taskRecurrency.monthRecurrency
        };

        this.getHasAreaShiftsForCompanyAsync();
        this.initializeDropdownOptions();

        // Period
        this.periodToggleChanged = this.periodToggleChanged.bind(this);
        this.onFromDateChanged = this.onFromDateChanged.bind(this);
        this.onUntilDateChanged = this.onUntilDateChanged.bind(this);
        // Recurrence
        this.onRecurrenceTabChanged = this.onRecurrenceTabChanged.bind(this);
        // Only once
        this.onOnlyOnceDateChanged = this.onOnlyOnceDateChanged.bind(this);
        // Weekly        
        this.onRecurEveryWeekChanged = this.onRecurEveryWeekChanged.bind(this);
        // Monthly
        this.onMonthlyTabChanged = this.onMonthlyTabChanged.bind(this);
        this.onSpecificDateDayOfMonthChanged = this.onSpecificDateDayOfMonthChanged.bind(this);
        this.onSpecificWeekDayOfMonthChanged = this.onSpecificWeekDayOfMonthChanged.bind(this);
        this.onSpecificWeekDayChanged = this.onSpecificWeekDayChanged.bind(this);
        this.onSpecificMonthChanged = this.onSpecificMonthChanged.bind(this);
    }

    private getHasAreaShiftsForCompanyAsync() {
        this.props.hasAreaShiftsForCompanyAsync();
    }

    private initializeDropdownOptions() {
        for (let i = 1; i < 32; ++i) {
            this.dropdownOptionsDaysOfMonth.push(<DropdownOption key={`monthdays-${i}`} value={i} label={this.getFormatedDateOrdinal(i)} />);
        }

        for (let i = 1; i < 13; ++i) {
            this.dropdownOptionsMonthsOfYear.push(<DropdownOption key={`monthmonths-${i}`} value={i} label={this.getFormatedDateOrdinal(i)} />);
        }

        for (let i = 1; i < 5; ++i) {
            this.dropdownOptionsWeedayOfMonth.push(<DropdownOption key={`weekdays-${i}`} value={i} label={this.getFormatedDateOrdinal(i)} />);
        }

        for (let i = 0; i < 7; ++i) {
            const uppedI = i + 1;
            // Sunday needs to be 0, so we start on 2018-07-01, which is a sunday
            this.dropdownOptionsWeekdayString.push(<DropdownOption key={`days-${uppedI}`} value={uppedI} label={moment(`2018-07-${(uppedI)}`).format("dddd")} />);
        }
    }

    private determineInitialPeriodToggle(taskRecurrency: TaskRecurrency): TaskRecurrencyPeriodToggle {
        if (taskRecurrency.fromDate || taskRecurrency.untilDate) {
            return TaskRecurrencyPeriodToggle.Period;
        }

        return TaskRecurrencyPeriodToggle.Infinite;
    }

    public periodToggleChanged(key: TaskRecurrencyPeriodToggle) {
        if (key === TaskRecurrencyPeriodToggle.Period) {
            this.updateTaskRecurrency({ ...this.state.taskRecurrency, fromDate: this.state.cachedFromDate, untilDate: this.state.cachedUntilDate });
            this.setState({
                cachedFromDate: undefined,
                cachedUntilDate: undefined
            });
        }
        else if (key === TaskRecurrencyPeriodToggle.Infinite) {
            this.setState({
                cachedFromDate: this.state.taskRecurrency.fromDate,
                cachedUntilDate: this.state.taskRecurrency.untilDate
            });
            this.updateTaskRecurrency({ ...this.state.taskRecurrency, fromDate: undefined, untilDate: undefined });
        }

        this.setState({ selectedPeriodToggleKey: key });
    }

    public isPeriodToggleBarDisabled() {
        return this.state.taskRecurrency.type === TaskRecurrencyType.noRecurrency;
    }

    public isPeriodDateDisabled() {
        return this.isPeriodToggleBarDisabled() || this.state.selectedPeriodToggleKey !== TaskRecurrencyPeriodToggle.Period;
    }

    public onFromDateChanged(fromDate: Date) {
        this.updateTaskRecurrency({ ...this.state.taskRecurrency, fromDate });
    }

    public onUntilDateChanged(untilDate: Date) {
        this.updateTaskRecurrency({ ...this.state.taskRecurrency, untilDate });
    }

    public onOnlyOnceDateChanged(executionDate: Date) {
        this.updateTaskRecurrency({ ...this.state.taskRecurrency, executionDate });
    }

    public onSpecificMonthChanged(month: number) {
        this.updateTaskRecurrency({ ...this.state.taskRecurrency, month });
    }

    public onSpecificDateDayOfMonthChanged(day: number) {
        this.updateTaskRecurrency({ ...this.state.taskRecurrency, day });
    }

    public onSpecificWeekDayOfMonthChanged(weekdayNumber: number) {
        this.updateTaskRecurrency({ ...this.state.taskRecurrency, weekdayNumber });
    }

    public onSpecificWeekDayChanged(weekday: number) {
        this.updateTaskRecurrency({ ...this.state.taskRecurrency, weekday });
    }

    public onRecurrenceTabChanged(type: TaskRecurrencyType) {
        this.updateTaskRecurrency({ ...this.state.taskRecurrency, type });
    }

    public onMonthlyTabChanged(monthRecurrency: TaskMonthRecurrency) {
        this.updateTaskRecurrency({ ...this.state.taskRecurrency, monthRecurrency });
    }

    public onRecurEveryWeekChanged(recurEveryWeekChanged: number) {
        this.updateTaskRecurrency({ ...this.state.taskRecurrency, week: recurEveryWeekChanged });
    }

    private updateTaskRecurrency(newTaskRecurrency: TaskRecurrency) {
        this.props.onRecurrencyChanged(newTaskRecurrency);
        this.setState({ taskRecurrency: newTaskRecurrency });
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> {
        const state: Partial<IState> = {};

        if (nextProps.shiftState.hasAreaShifts !== undefined && nextProps.shiftState.hasAreaShifts !== prevState.hasAreaShifts) {
            state.hasAreaShifts = nextProps.shiftState.hasAreaShifts;
            nextProps.getCompanyShiftsAsync();
            if (prevState.area) {
                nextProps.getAreaShiftsAsync(prevState.area.id);
            }
        }

        if (nextProps.shiftState.companyShifts && nextProps.shiftState.companyShifts !== prevState.companyShifts) {
            state.companyShifts = nextProps.shiftState.companyShifts;
            if (nextProps.shiftState.hasAreaShifts !== undefined && !nextProps.shiftState.hasAreaShifts || !prevState.areaShifts.length) {
                state.shifts = state.companyShifts;
            }
        }

        if (nextProps.area && nextProps.area !== prevState.area) {
            state.area = nextProps.area;
            nextProps.getAreaShiftsAsync(nextProps.area.id);
        }

        if (nextProps.shiftState.areaShifts && nextProps.shiftState.areaShifts !== prevState.areaShifts) {
            state.areaShifts = nextProps.shiftState.areaShifts;
            nextProps.shiftState.areaShifts.length ? state.shifts = nextProps.shiftState.areaShifts : state.shifts = prevState.companyShifts;
        }

        if (nextProps.taskRecurrency !== prevState.taskRecurrency) {
            state.taskRecurrency = nextProps.taskRecurrency;
        }

        return state;
    }

    public getFormatedDateOrdinal(day: number) {
        // Am ordinal is the name for a day in the month with the format 1st/2nd/3rd/1e/2e/3e
        // We need a month with 31 days so we take a random fixed month with 31 days
        // Oktober of 2012 has 31 days and the 1st day is a monday
        return moment(`2012-10-${day}`).format("Do");
    }

    public getNextExecutionStringSpecificDate(day: number, now: moment.Moment): string {
        let executionDate = now;
        if (day < executionDate.date()) {
            executionDate = executionDate.add((1), "M");
        }

        return this.getNextExecutionString(day, executionDate);
    }

    private getGivenDateOfMonth(startDate: moment.Moment, dayOfWeek: number, weekNumber: number) {
        // Start of the month of the given startDate
        const myMonth = moment(startDate).startOf("month");
        // dayOfWeek of the first week of the month
        const firstDayOfWeek = myMonth.clone().day(dayOfWeek);
        // Check if first firstDayOfWeek is in the given month
        if (firstDayOfWeek.month() !== myMonth.month()) {
            weekNumber++;
        }
        // Return result
        return firstDayOfWeek.add(weekNumber - 1, "weeks");
    }

    public getNextExecutionStringSpecificWeek(dayNumber: number | undefined, day: number | undefined, now: moment.Moment): string {
        dayNumber = dayNumber || 1;
        day = day || 1;

        let executionDate = this.getGivenDateOfMonth(now, day - 1, dayNumber);
        if (executionDate < now) {
            executionDate = this.getGivenDateOfMonth(now.add(1, "M"), day - 1, dayNumber);
        }

        return this.getNextExecutionString(undefined, executionDate);
    }

    private getNextExecutionString(day: number | undefined, executionDate: moment.Moment): string {
        const nextExecutionString = i18n.t("taskDetail.recurrence.nextExecution.nextDate");
        return nextExecutionString.replace("{day}", this.getFormatedDateOrdinal(day || executionDate.date())).replace("{month}", executionDate.format("MMMM")).replace("{year}", executionDate.format("YYYY"));
    }

    public render() {
        if (this.state.hasAreaShifts && (!this.state.shifts || !this.state.shifts.length)) {
            return (
                <div className={styles.taskTemplateRecurrency}>
                    <div className={styles.selectArea}>
                        <h2>{i18n.t("shiftCalendar.selectArea")}</h2>
                    </div>
                </div>
            );
        }

        return (
            <ContentView>
                <div className={styles.taskTemplateRecurrency}>
                    <div className={styles.header}>
                        <div className={styles.title}>
                            <Repeat className={styles.icon} />
                            <h2>{i18n.t("taskDetail.recurrence.title")}</h2>
                        </div>
                        <div className={styles.period}>
                            <ToggleBar selectedItem={this.state.selectedPeriodToggleKey} onChange={this.periodToggleChanged} className={styles.togglebar} disabled={this.isPeriodToggleBarDisabled()}>
                                <ToggleOption value={TaskRecurrencyPeriodToggle.Infinite}><Cached /></ToggleOption>
                                <ToggleOption value={TaskRecurrencyPeriodToggle.Period}>{i18n.t("taskDetail.recurrence.toggleTitles.fixedPeriod")}</ToggleOption>
                            </ToggleBar>
                            <span className={this.isPeriodDateDisabled() ? styles.disabled : null}>From:</span>
                            <Datepicker
                                className={`${styles.datepicker} ${styles.inline} ${styles.first}`}
                                inputClassName={styles.small}
                                selectedDate={this.state.taskRecurrency.fromDate}
                                onChange={this.onFromDateChanged}
                                disabled={this.isPeriodDateDisabled()} />
                            <span className={this.isPeriodDateDisabled() ? styles.disabled : null}>To:</span>
                            <Datepicker
                                className={`${styles.datepicker} ${styles.inline}`}
                                inputClassName={styles.small}
                                selectedDate={this.state.taskRecurrency.untilDate}
                                onChange={this.onUntilDateChanged}
                                disabled={this.isPeriodDateDisabled()} />
                        </div>
                    </div>
                    <div className={styles.recurrency}>
                        <Tabview tabviewMode={TabviewMode.Vertical} onChange={this.onRecurrenceTabChanged} selectedItem={this.state.taskRecurrency.type}>
                            <TabPane id="tbpTaskTemplateRecurrencyOnlyOnce" className={styles.verticalTabPane} value={TaskRecurrencyType.noRecurrency} title={i18n.t("taskDetail.recurrence.tabTitles.onlyOnce")}>
                                <div className={styles.onlyOnce}>
                                    <Datepicker
                                        className={styles.datepicker}
                                        selectedDate={this.state.taskRecurrency.executionDate}
                                        onChange={this.onOnlyOnceDateChanged} />
                                    <ShiftCalendar id="shcTaskTemplateOnlyOnce" className={styles.shiftCalendar} taskRecurrency={this.state.taskRecurrency} shifts={this.state.shifts} shiftCalendarMode={ShiftCalendarMode.ONLY_ONCE} selectedDate={this.state.taskRecurrency.executionDate} isReadonly={false} onRecurrencyChanged={this.props.onRecurrencyChanged} />
                                </div>
                            </TabPane>
                            <TabPane id="tbpTaskTemplateRecurrencyCertainShifts" className={styles.verticalTabPane} value={TaskRecurrencyType.shifts} title={i18n.t("taskDetail.recurrence.tabTitles.certainShifts")}>
                                <ShiftCalendar id="shcTaskTemplateCertainShifts" className={styles.shiftCalendar} taskRecurrency={this.state.taskRecurrency} shifts={this.state.shifts} shiftCalendarMode={ShiftCalendarMode.SHIFTS} isReadonly={false} onRecurrencyChanged={this.props.onRecurrencyChanged} />
                            </TabPane>
                            <TabPane id="tbpTaskTemplateRecurrencyDailyWeekly" className={styles.verticalTabPane} value={TaskRecurrencyType.week} title={i18n.t("taskDetail.recurrence.tabTitles.dailyWeekly")}>
                                <div className={styles.form}>
                                    <span>{i18n.t("taskDetail.recurrence.weekly.recurEvery")}</span>
                                    <NumericTextbox className={styles.numericTextBox} onChange={this.onRecurEveryWeekChanged} value={this.state.taskRecurrency.week} min={1} />
                                    <span>{i18n.t("taskDetail.recurrence.weekly.week")}</span>
                                </div>

                                <ShiftCalendar id="shcTaskTemplateDailyWeekly" className={styles.shiftCalendar} taskRecurrency={this.state.taskRecurrency} shifts={this.state.shifts} shiftCalendarMode={ShiftCalendarMode.DAILY_WEEKLY} isReadonly={false} onRecurrencyChanged={this.props.onRecurrencyChanged} />
                            </TabPane>
                            <TabPane id="tbpTaskTemplateRecurrencyMonthly" className={styles.verticalTabPane} value={TaskRecurrencyType.month} title={i18n.t("taskDetail.recurrence.tabTitles.monthly")}>
                                <Tabview tabviewMode={TabviewMode.Horizontal} onChange={this.onMonthlyTabChanged} selectedItem={this.state.taskRecurrency.monthRecurrency}>
                                    <TabPane className={styles.horizontalTabPane} value={TaskMonthRecurrency.DayOfMonth} title={i18n.t("taskDetail.recurrence.tabTitles.specificDate")}>
                                        <div className={styles.form}>
                                            <span>{i18n.t("taskDetail.recurrence.nextExecution.every")}</span>
                                            <Dropdown onChange={this.onSpecificDateDayOfMonthChanged} selectedItem={this.state.taskRecurrency.day} className={styles.dropdown}>
                                                {this.dropdownOptionsDaysOfMonth}
                                            </Dropdown>
                                            {i18n.t("taskDetail.recurrence.nextExecution.dayOfEvery")}
                                            <Dropdown onChange={this.onSpecificMonthChanged} selectedItem={this.state.taskRecurrency.month} className={styles.dropdown}>
                                                {this.dropdownOptionsMonthsOfYear}
                                            </Dropdown>
                                            {i18n.t("taskDetail.recurrence.nextExecution.month")}
                                        </div>
                                        <div className={styles.nextExecution}>
                                            <ErrorOutline className={styles.icon} />
                                            {this.getNextExecutionStringSpecificDate(this.state.taskRecurrency.day, moment())}
                                        </div>
                                    </TabPane>
                                    <TabPane className={styles.horizontalTabPane} value={TaskMonthRecurrency.Weekday} title={i18n.t("taskDetail.recurrence.tabTitles.specificWeek")}>
                                        <div className={styles.form}>
                                            {i18n.t("taskDetail.recurrence.nextExecution.every")}
                                            <Dropdown onChange={this.onSpecificWeekDayOfMonthChanged} selectedItem={this.state.taskRecurrency.weekdayNumber} className={styles.dropdown}>
                                                {this.dropdownOptionsWeedayOfMonth}
                                            </Dropdown>
                                            <Dropdown onChange={this.onSpecificWeekDayChanged} selectedItem={this.state.taskRecurrency.weekday} className={`${styles.dropdown} ${styles.noMarginLeft}`}>
                                                {this.dropdownOptionsWeekdayString}
                                            </Dropdown>
                                            {i18n.t("taskDetail.recurrence.nextExecution.ofEvery")}
                                            <Dropdown onChange={this.onSpecificMonthChanged} selectedItem={this.state.taskRecurrency.month} className={styles.dropdown}>
                                                {this.dropdownOptionsMonthsOfYear}
                                            </Dropdown>
                                            {i18n.t("taskDetail.recurrence.nextExecution.month")}
                                        </div>
                                        <div className={styles.nextExecution}>
                                            <ErrorOutline className={styles.icon} />{this.getNextExecutionStringSpecificWeek(this.state.taskRecurrency.weekdayNumber, this.state.taskRecurrency.weekday, moment())}
                                        </div>
                                    </TabPane>
                                </Tabview>
                            </TabPane>
                        </Tabview>
                    </div>
                </div>
            </ContentView>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(TaskTemplateRecurrencyView);


// WEBPACK FOOTER //
// ./src/Components/Pages/TaskTemplateDetailView/TaskTemplateRecurrencyView/TaskTemplateRecurrencyView.tsx